// Input for the 3D view: camera (grab-pan / wheel-zoom / Q-E spin) plus raycast
// picking that drives the SAME domain commands as the 2D handler — select, move,
// attack, build, rally, demolish. It only reads GameState and calls send(); the
// selection/command semantics mirror client/input.ts. Owner: client-render3d.

import { Mesh, type Object3D, PerspectiveCamera, Raycaster, Scene, Vector2, Vector3 } from 'three'
import { BUILDINGS, buildingFootprint, isBoxOrder, isPaintable } from '../../shared/constants'
import { hexToPixel, lerp, pixelToHex } from '../../shared/hex'
import type { ClientMsg } from '../../shared/protocol'
import type { Building, UnitWire } from '../../shared/types'
import { HEX_SIZE, type GameState } from '../state'
import { isSoldier } from '../input'
import { paintBegin, paintCancel, paintCommit, paintExtend } from '../paint'
import { boxOrderCells, boxOrderCommit, orderClick } from '../chopBox'
import { resolvePier } from '../pier'
import { rampDir } from '../ramp'
import { FOV, MAX_DIST, MIN_DIST, SPIN_SPEED, groundBasis, type OrbitCam } from './camera'
import { tileHeight } from './terrainMesh'

const S = HEX_SIZE
const HEX_RAD = Math.PI / 3
const CLICK_PX = 5 // pointer travel under which a press counts as a click, not a drag
/** WASD/arrow pan speed as a fraction of camera distance per second (zoom-consistent). */
const PAN_RATE = 1.35

const PAN_KEYS: Record<string, 'up' | 'down' | 'left' | 'right'> = {
  w: 'up', arrowup: 'up', s: 'down', arrowdown: 'down',
  a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right',
}

type Pick = { kind: 'unit' | 'building'; id: number }

interface Ctx {
  canvas: HTMLCanvasElement
  st: GameState
  send: (m: ClientMsg) => void
  camera: PerspectiveCamera
  scene: Scene
  orbit: OrbitCam
}

export function bindInput3d(ctx: Ctx): void {
  const { canvas, st, send, camera, scene, orbit } = ctx
  const ray = new Raycaster()
  const ndc = new Vector2()

  function isHostileOwner(owner: string): boolean {
    if (!st.me || owner === st.me) return false
    const mine = st.factions.get(st.me)
    return !mine || !mine.allies.includes(owner)
  }
  function buildingAt(q: number, r: number): Building | null {
    for (const b of st.buildings.values()) {
      for (const c of buildingFootprint(b.type, b.q, b.r)) if (c.q === q && c.r === r) return b
    }
    return null
  }

  // Raycast the scene once; return the nearest entity hit and the nearest ground hit.
  function cast(cx: number, cy: number): { ent: Pick | null; hex: { q: number; r: number } | null } {
    const r = canvas.getBoundingClientRect()
    ndc.x = ((cx - r.left) / r.width) * 2 - 1
    ndc.y = -((cy - r.top) / r.height) * 2 + 1
    ray.setFromCamera(ndc, camera)
    // Recursive: serfs are now articulated Groups, so their limb meshes (which carry
    // userData.pick) are scene descendants, not top-level. Terrain/buildings are still
    // top-level and hit directly; overlay meshes carry no pick/terrain so they're ignored.
    const hits = ray.intersectObjects(scene.children, true)
    let ent: Pick | null = null
    let hex: { q: number; r: number } | null = null
    for (const h of hits) {
      if (!ent) {
        // A serf is an articulated Group whose limb meshes carry no tag; the pick lives
        // on the root, so climb parents from the hit object to find it.
        for (let o: Object3D | null = h.object; o; o = o.parent) {
          const pk = o.userData.pick as Pick | undefined
          if (pk) { ent = pk; break }
        }
      }
      if (h.object.userData.terrain && !hex) {
        // Read the hex from the hit TRIANGLE (each vertex is tagged), not the point —
        // so hovering a cliff wall reports its own hex, not the neighbour it rounds to.
        const ha = (h.object as Mesh).geometry.getAttribute('hex')
        if (ha && h.face) hex = { q: ha.getX(h.face.a), r: ha.getY(h.face.a) }
        else { const p = h.point; hex = pixelToHex(p.x, p.z, S) }
      }
      if (ent && hex) break
    }
    // Fall back to the horizontal plane through the target when the ray misses terrain.
    if (!hex) {
      const o = ray.ray.origin, d = ray.ray.direction
      if (Math.abs(d.y) > 1e-5) { const t = -o.y / d.y; if (t > 0) hex = pixelToHex(o.x + d.x * t, o.z + d.z * t, S) }
    }
    return { ent, hex }
  }

  // ---- selection & commands (mirror client/input.ts) --------------------------

  function selectOwn(u: UnitWire, additive: boolean): void {
    if (additive && st.selection.units.length) {
      const has = st.selection.units.includes(u.id)
      st.selection = {
        units: has ? st.selection.units.filter((id) => id !== u.id) : [...st.selection.units, u.id],
        building: null,
      }
    } else {
      st.selection = { units: [u.id], building: null }
    }
  }

  function clickSelect(pick: Pick | null, additive: boolean): void {
    if (pick?.kind === 'unit') {
      const u = st.units.get(pick.id)
      if (!u) return
      if (u.owner === st.me) selectOwn(u, additive)
      else st.selection = { units: [u.id], building: null }
      return
    }
    if (pick?.kind === 'building') { st.selection = { units: [], building: pick.id }; return }
    if (!additive) st.selection = { units: [], building: null }
  }

  function sendBuild(q: number, r: number): void {
    if (!st.buildMode) return
    if (st.buildMode === 'pier') {
      const plan = resolvePier(st, q, r, st.pierRotate)
      if (plan) { send({ t: 'build', type: 'pier', q: plan.q, r: plan.r, dir: plan.dir }); return }
    }
    if (st.buildMode === 'ramp') {
      const dir = rampDir(st, q, r, st.pierRotate)
      if (dir !== null) { send({ t: 'build', type: 'ramp', q, r, dir }); return }
    }
    if (st.buildMode === 'farm') {
      send({ t: 'build', type: 'farm', q, r, dir: ((st.pierRotate % 6) + 6) % 6 })
      return
    }
    send({ t: 'build', type: st.buildMode, q, r })
  }

  function command(pick: Pick | null, hex: { q: number; r: number } | null): void {
    if (st.buildMode) { st.buildMode = null; return }
    if (!hex) return
    if (st.selection.units.length) {
      const ids = st.selection.units.filter((id) => st.units.get(id)?.owner === st.me)
      if (!ids.length) return
      let target: { kind: 'u' | 'b'; id: number } | null = null
      let tq = hex.q, tr = hex.r
      if (pick?.kind === 'unit') {
        const u = st.units.get(pick.id)
        if (u && isHostileOwner(u.owner)) { target = { kind: 'u', id: u.id }; tq = u.q; tr = u.r }
      } else if (pick?.kind === 'building') {
        const b = st.buildings.get(pick.id)
        if (b && isHostileOwner(b.owner)) target = { kind: 'b', id: b.id }
      }
      if (!target) { const b = buildingAt(hex.q, hex.r); if (b && isHostileOwner(b.owner)) target = { kind: 'b', id: b.id } }
      const hasMilitary = ids.some((id) => { const un = st.units.get(id); return !!un && isSoldier(un) })
      if (target && hasMilitary) send({ t: 'cmd', ids, action: 'attack', q: tq, r: tr, target })
      else send({ t: 'cmd', ids, action: 'move', q: hex.q, r: hex.r })
      return
    }
    if (st.selection.building !== null) {
      const b = st.buildings.get(st.selection.building)
      if (b && b.owner === st.me && (BUILDINGS[b.type].trains?.length || b.type === 'townhall')) {
        send({ t: 'rally', buildingId: b.id, q: hex.q, r: hex.r })
      }
    }
  }

  // ---- pointer: left click = select/command, left drag = box select,
  //      middle/right drag = grab-pan ------------------------------------------

  // marquee rectangle (DOM overlay; the 3D scene never sees it)
  const marquee = document.createElement('div')
  marquee.style.cssText =
    'position:fixed;display:none;pointer-events:none;z-index:3;' +
    'border:1px solid rgba(159,242,255,.9);background:rgba(159,242,255,.12)'
  document.body.appendChild(marquee)

  /** Select every own unit whose screen position falls inside the box (soldiers and
   *  workers alike); shift adds to the current selection. */
  function boxSelect(x0: number, y0: number, x1: number, y1: number, additive: boolean): void {
    const minX = Math.min(x0, x1), maxX = Math.max(x0, x1)
    const minY = Math.min(y0, y1), maxY = Math.max(y0, y1)
    const ids = additive ? st.selection.units.filter((id) => st.units.has(id)) : []
    const v = new Vector3()
    for (const u of st.units.values()) {
      if (u.owner !== st.me) continue
      const a = hexToPixel(u.fq, u.fr, S), b = hexToPixel(u.q, u.r, S)
      const mp = Math.max(0, Math.min(1, u.mp))
      const y = lerp(tileHeight(st, u.fq, u.fr), tileHeight(st, u.q, u.r), mp) + 10
      v.set(lerp(a.x, b.x, mp), y, lerp(a.y, b.y, mp)).project(camera)
      if (v.z >= 1) continue // behind the camera
      const sx = (v.x * 0.5 + 0.5) * window.innerWidth
      const sy = (-v.y * 0.5 + 0.5) * window.innerHeight
      if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY && !ids.includes(u.id)) ids.push(u.id)
    }
    st.selection = { units: ids, building: null }
  }

  canvas.addEventListener('contextmenu', (e) => e.preventDefault())
  let down: { x: number; y: number; button: number; moved: boolean } | null = null
  let last = { x: 0, y: 0 }
  // Paint orders: press-and-drag lays a run of the current order — roads, digs, or
  // fills. The stroke only HIGHLIGHTS as you drag (st.paintPath); nothing is sent until
  // you release, and back-dragging un-highlights the tail. See paint.ts.
  let painting = false
  // Box orders (chop + terraform) use a rubber-band BOX instead of a path: drag a
  // rectangle; every eligible hex whose center projects inside it is selected. See chopBox.ts.
  let boxOrdering = false
  const vProj = new Vector3()
  /** Project a hex center (at its ground height) to viewport pixels, matching the
   *  marquee's clientX/clientY space; vis=false when it's behind the camera. */
  function hexScreen(q: number, r: number): { x: number; y: number; vis: boolean } {
    const p = hexToPixel(q, r, S)
    vProj.set(p.x, tileHeight(st, q, r) + 6, p.y).project(camera)
    return { x: (vProj.x * 0.5 + 0.5) * window.innerWidth, y: (-vProj.y * 0.5 + 0.5) * window.innerHeight, vis: vProj.z < 1 }
  }

  canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') return // raw touch handlers own touch input
    down = { x: e.clientX, y: e.clientY, button: e.button, moved: false }
    last = { x: e.clientX, y: e.clientY }
    try { canvas.setPointerCapture(e.pointerId) } catch { /* older browsers */ }
    if (e.button === 0 && st.buildMode && isBoxOrder(st.buildMode)) {
      // Start a box order: highlight fills in as you drag; committed on release.
      boxOrdering = true
      st.paintType = st.buildMode
      st.paintPath = []
    } else if (e.button === 0 && st.buildMode && isPaintable(st.buildMode)) {
      const h = cast(e.clientX, e.clientY).hex
      if (h) { painting = true; paintBegin(st, st.buildMode, h.q, h.r) }
    }
  })

  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') return
    // hover hex for the build ghost / cursor feedback + entity for the silhouette
    const c = cast(e.clientX, e.clientY)
    st.hover = c.hex
    st.hoverEnt = c.ent
    st.mouse = { x: e.clientX, y: e.clientY }
    if (!down) return
    const dx = e.clientX - last.x, dy = e.clientY - last.y
    if (!down.moved && Math.hypot(e.clientX - down.x, e.clientY - down.y) > CLICK_PX) down.moved = true
    if (!down.moved) return
    last = { x: e.clientX, y: e.clientY }
    if (boxOrdering && st.paintType) {
      // Stretch the rubber-band box and re-select every eligible hex inside it. The
      // preview (st.paintPath) commits on release; never pans while boxing.
      marquee.style.display = 'block'
      marquee.style.left = Math.min(down.x, e.clientX) + 'px'
      marquee.style.top = Math.min(down.y, e.clientY) + 'px'
      marquee.style.width = Math.abs(e.clientX - down.x) + 'px'
      marquee.style.height = Math.abs(e.clientY - down.y) + 'px'
      const minX = Math.min(down.x, e.clientX), maxX = Math.max(down.x, e.clientX)
      const minY = Math.min(down.y, e.clientY), maxY = Math.max(down.y, e.clientY)
      st.paintPath = boxOrderCells(st, st.paintType, (q, r) => {
        const s = hexScreen(q, r)
        return s.vis && s.x >= minX && s.x <= maxX && s.y >= minY && s.y <= maxY
      }, st.chopCancel)
      return
    }
    if (painting && st.buildMode && isPaintable(st.buildMode)) {
      // Extend the highlighted stroke to the hex under the cursor (back-drag trims it);
      // never pan while painting. Nothing is sent until release.
      if (st.hover) paintExtend(st, st.hover.q, st.hover.r)
      return
    }
    if (down.button === 0 && !st.buildMode) {
      // left drag = box select: stretch the marquee
      marquee.style.display = 'block'
      marquee.style.left = Math.min(down.x, e.clientX) + 'px'
      marquee.style.top = Math.min(down.y, e.clientY) + 'px'
      marquee.style.width = Math.abs(e.clientX - down.x) + 'px'
      marquee.style.height = Math.abs(e.clientY - down.y) + 'px'
    } else {
      // grab-pan: world follows the cursor, so the target moves opposite
      const k = (2 * orbit.dist * Math.tan((FOV * Math.PI) / 180 / 2)) / window.innerHeight
      const b = groundBasis(camera)
      st.cam.x -= (b.rx * dx + b.fx * dy) * k
      st.cam.y -= (b.rz * dx + b.fz * dy) * k
    }
  })

  const endPointer = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return
    if (!down) return
    const d = down; down = null
    if (boxOrdering) {
      const type = st.paintType
      boxOrdering = false
      marquee.style.display = 'none'
      if (type && d.moved) boxOrderCommit(st, send, type, st.chopCancel)
      else { const h = cast(e.clientX, e.clientY).hex; if (h && type) orderClick(st, send, type, h.q, h.r); st.paintPath = []; st.paintType = null }
      return // tool stays armed for the next box
    }
    if (painting) {
      // Release commits the whole highlighted stroke at once (valid hexes only). The
      // tool stays armed so the next stroke starts a new run (Esc / re-click exits).
      painting = false
      paintCommit(st, send)
      return
    }
    if (d.moved) {
      if (d.button === 0 && !st.buildMode) {
        marquee.style.display = 'none'
        boxSelect(d.x, d.y, e.clientX, e.clientY, e.shiftKey)
      }
      return // drags never fall through to click/command handling
    }
    const { ent, hex } = cast(e.clientX, e.clientY)
    if (d.button === 0) {
      if (st.buildMode && hex) {
        sendBuild(hex.q, hex.r)
        if (!isPaintable(st.buildMode) && !e.shiftKey) st.buildMode = null
      } else {
        clickSelect(ent, e.shiftKey)
      }
    } else if (d.button === 2) {
      command(ent, hex)
    }
  }
  canvas.addEventListener('pointerup', endPointer)
  canvas.addEventListener('pointercancel', () => { down = null; painting = false; boxOrdering = false; paintCancel(st); marquee.style.display = 'none' })

  canvas.addEventListener('wheel', (e) => {
    e.preventDefault()
    orbit.dist = Math.max(MIN_DIST, Math.min(MAX_DIST, orbit.dist * Math.exp(e.deltaY * 0.0012)))
  }, { passive: false })

  // ---- touch: tap select · drag pan · pinch zoom/rotate · long-press command ----
  // Raw Touch Events (passive:false) mirroring the 2.5D conventions; preventDefault
  // suppresses synthetic mouse events and page scroll, and the pointer handlers
  // above ignore pointerType 'touch', so nothing double-fires.
  const tpts = new Map<number, { x: number; y: number }>()
  let tMode: 'none' | 'tap' | 'pan' | 'pinch' = 'none'
  let tStart = { x: 0, y: 0 }
  let tLast = { x: 0, y: 0 }
  let pinch0 = 0
  let dist0 = 0
  let angle0 = 0
  let yaw0 = 0
  let lpTimer: ReturnType<typeof setTimeout> | null = null
  const clearLp = (): void => { if (lpTimer !== null) { clearTimeout(lpTimer); lpTimer = null } }
  const twoFinger = (): { d: number; a: number } => {
    const [p1, p2] = [...tpts.values()]
    return { d: Math.hypot(p2.x - p1.x, p2.y - p1.y), a: Math.atan2(p2.y - p1.y, p2.x - p1.x) }
  }

  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) tpts.set(t.identifier, { x: t.clientX, y: t.clientY })
    if (tpts.size === 1) {
      tMode = 'tap'
      const t = e.changedTouches[0]
      tStart = { x: t.clientX, y: t.clientY }
      tLast = { ...tStart }
      clearLp()
      lpTimer = setTimeout(() => {
        if (tMode !== 'tap') return
        tMode = 'none'
        if (navigator.vibrate) navigator.vibrate(15)
        const { ent, hex } = cast(tStart.x, tStart.y)
        command(ent, hex) // long-press = the right-click verb
      }, 450)
    } else if (tpts.size === 2) {
      clearLp()
      tMode = 'pinch'
      const { d, a } = twoFinger()
      pinch0 = d || 1
      dist0 = orbit.dist
      angle0 = a
      yaw0 = orbit.yaw
    }
  }, { passive: false })

  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) {
      if (tpts.has(t.identifier)) tpts.set(t.identifier, { x: t.clientX, y: t.clientY })
    }
    if (tMode === 'tap') {
      const t = e.changedTouches[0]
      if (Math.hypot(t.clientX - tStart.x, t.clientY - tStart.y) > 9) {
        tMode = 'pan'
        clearLp()
        tLast = { x: t.clientX, y: t.clientY }
      } else {
        // still finger: preview the hovered hex (build ghost) under it
        const c = cast(tStart.x, tStart.y)
        st.hover = c.hex
        st.hoverEnt = c.ent
      }
    } else if (tMode === 'pan' && tpts.size === 1) {
      const t = e.changedTouches[0]
      const dx = t.clientX - tLast.x, dy = t.clientY - tLast.y
      tLast = { x: t.clientX, y: t.clientY }
      const k = (2 * orbit.dist * Math.tan((FOV * Math.PI) / 180 / 2)) / window.innerHeight
      const b = groundBasis(camera)
      st.cam.x -= (b.rx * dx + b.fx * dy) * k
      st.cam.y -= (b.rz * dx + b.fz * dy) * k
    } else if (tMode === 'pinch' && tpts.size >= 2) {
      const { d, a } = twoFinger()
      orbit.dist = Math.max(MIN_DIST, Math.min(MAX_DIST, dist0 * (pinch0 / Math.max(1, d))))
      orbit.yaw = yaw0 + (a - angle0) // twist rotates the view
      orbit.yawTarget = orbit.yaw
      orbit.spinVel = 0
    }
  }, { passive: false })

  const touchDone = (e: TouchEvent): void => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) tpts.delete(t.identifier)
    if (tMode === 'tap' && tpts.size === 0) {
      clearLp()
      const { ent, hex } = cast(tStart.x, tStart.y)
      if (st.buildMode && isBoxOrder(st.buildMode) && hex) orderClick(st, send, st.buildMode, hex.q, hex.r) // tap = one hex (queue or remove)
      else if (st.buildMode && hex) sendBuild(hex.q, hex.r) // stays armed for repeat placement
      else clickSelect(ent, false)
    }
    if (tpts.size === 0) tMode = 'none'
    else if (tpts.size === 1 && tMode === 'pinch') {
      // one finger lifted mid-pinch: continue as a pan from the survivor
      const p = [...tpts.values()][0]
      tMode = 'pan'
      tLast = { ...p }
    }
  }
  canvas.addEventListener('touchend', touchDone, { passive: false })
  canvas.addEventListener('touchcancel', touchDone, { passive: false })

  // touch-only rotate chip for oriented buildings (pier/ramp), same as 2.5D
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    const chip = document.createElement('button')
    chip.className = 'pier-rotate-chip'
    chip.textContent = '⟳ rotate'
    chip.style.display = 'none'
    chip.addEventListener('pointerdown', () => { st.pierRotate++ })
    document.body.appendChild(chip)
    setInterval(() => {
      chip.style.display = st.buildMode === 'pier' || st.buildMode === 'ramp' || st.buildMode === 'farm' ? '' : 'none'
    }, 300)
  }

  // ---- keyboard ---------------------------------------------------------------

  function typing(): boolean {
    const el = document.activeElement as HTMLElement | null
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
  }
  const panHeld = new Set<'up' | 'down' | 'left' | 'right'>()
  let shiftHeld = false

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Shift') shiftHeld = true
    if (typing()) return
    const k = e.key.toLowerCase()
    const pan = PAN_KEYS[k]
    if (pan) { panHeld.add(pan); e.preventDefault(); return }
    if (k === 'q' || k === 'e') {
      e.preventDefault()
      const dir = k === 'e' ? 1 : -1
      if (e.shiftKey) {
        // Shift = snap to the next 60° facing in that direction (one step per press).
        if (e.repeat) return
        orbit.spinVel = 0
        orbit.yawTarget = (dir > 0 ? Math.floor(orbit.yaw / HEX_RAD) + 1 : Math.ceil(orbit.yaw / HEX_RAD) - 1) * HEX_RAD
      } else {
        // Held = free continuous spin; it lands wherever released (no snap).
        orbit.spinVel = dir * SPIN_SPEED
      }
      return
    }
    if (k === 'b') { if (!st.observer) st.emit('ui:build'); return }
    if (k === 'r') { if (st.buildMode === 'pier' || st.buildMode === 'ramp' || st.buildMode === 'farm') st.pierRotate++; else st.emit('ui:roster'); return }
    if (k === 'p') { st.emit('ui:diplo'); return }
    if (k === 'h') { st.emit('ui:help'); return }
    if (k === 'f') { focusSelection(); return }
    if (e.key === 'Escape') {
      painting = false
      boxOrdering = false
      marquee.style.display = 'none'
      paintCancel(st) // drop any in-progress drag highlight
      if (st.buildMode) st.buildMode = null
      else st.selection = { units: [], building: null }
      return
    }
    if (e.key === 'Delete') {
      const id = st.selection.building
      if (id !== null) {
        const b = st.buildings.get(id)
        if (b && b.owner === st.me) { send({ t: 'demolish', id }); st.selection = { units: [], building: null } }
      }
    }
  })

  window.addEventListener('keyup', (e) => {
    if (e.key === 'Shift') shiftHeld = false
    const k = e.key.toLowerCase()
    if (k === 'q' || k === 'e') orbit.spinVel = 0 // releasing Q/E ends a free spin
    const pan = PAN_KEYS[k]
    if (pan) panHeld.delete(pan)
  })
  // A lost focus (alt-tab) must not leave the camera spinning or panning forever — nor
  // a half-finished drag-paint stroke stranded (the pointerup may never arrive).
  window.addEventListener('blur', () => {
    orbit.spinVel = 0; panHeld.clear(); shiftHeld = false
    down = null; painting = false; boxOrdering = false; paintCancel(st); marquee.style.display = 'none'
  })

  // Held WASD/arrows pan the ground target along the current screen axes (so it
  // tracks the Q/E facing), at a zoom-consistent speed.
  let lastPan = performance.now()
  function panLoop(t: number): void {
    const dt = Math.min(0.05, (t - lastPan) / 1000)
    lastPan = t
    if (panHeld.size) {
      let f = 0, rt = 0
      if (panHeld.has('up')) f += 1
      if (panHeld.has('down')) f -= 1
      if (panHeld.has('right')) rt += 1
      if (panHeld.has('left')) rt -= 1
      if (f || rt) {
        const n = Math.hypot(f, rt)
        // Half speed by default; hold Shift for full (the old) speed.
        const speed = orbit.dist * PAN_RATE * (shiftHeld ? 1 : 0.5) * dt
        const b = groundBasis(camera)
        st.cam.x += (b.fx * f + b.rx * rt) / n * speed
        st.cam.y += (b.fz * f + b.rz * rt) / n * speed
      }
    }
    requestAnimationFrame(panLoop)
  }
  requestAnimationFrame(panLoop)

  function focusSelection(): void {
    let tx: number | null = null, ty = 0
    if (st.selection.units.length) {
      let x = 0, y = 0, n = 0
      for (const id of st.selection.units) {
        const u = st.units.get(id); if (!u) continue
        const p = hexToPixel(u.q, u.r, S); x += p.x; y += p.y; n++
      }
      if (n) { tx = x / n; ty = y / n }
    } else if (st.selection.building !== null) {
      const b = st.buildings.get(st.selection.building)
      if (b) { const p = hexToPixel(b.q, b.r, S); tx = p.x; ty = p.y }
    }
    if (tx === null && st.home) { const p = hexToPixel(st.home.q, st.home.r, S); tx = p.x; ty = p.y }
    if (tx !== null) { st.cam.x = tx; st.cam.y = ty }
  }
}
